import React, { Component } from 'react'
import {
    Alert, Button, Card, Col, Descriptions, Drawer, Form, Input, message, Modal, Progress,
    Row, Select, Space, Table, Tabs, Tag, Typography
} from 'antd'
import { DownloadOutlined, FileTextOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import {
    createAppBuildTask, getAppBuildArtifactDownload, getAppBuildJobArtifacts, getAppBuildJobLogs,
    getAppBuildProjects, getAppBuildTaskDetail, getAppBuildTasks, validateAppBuildTask,
} from '../../request'
import AppBuildProjectConfig from './AppBuildProjectConfig'
import './index.less'

const { Option } = Select
const { Paragraph, Text, Title } = Typography
const { TextArea } = Input
const { TabPane } = Tabs

const STATUS = {
    QUEUED: { text: '排队中', color: 'default' },
    PENDING: { text: '待领取', color: 'default' },
    RUNNING: { text: '构建中', color: 'processing' },
    SUCCEEDED: { text: '成功', color: 'success' },
    FAILED: { text: '失败', color: 'error' },
    CANCELLED: { text: '已取消', color: 'default' },
}

export default class AppBuilderManagement extends Component {
    formRef = React.createRef()

    state = {
        loading: false,
        projects: [],
        projectKey: undefined,
        status: undefined,
        dataSource: [],
        pagination: { current: 1, pageSize: 20, total: 0 },
        createVisible: false,
        createProjectKey: undefined,
        validating: false,
        creating: false,
        validation: null,
        detailVisible: false,
        detailLoading: false,
        detail: null,
        logsVisible: false,
        logsLoading: false,
        logs: [],
        logJob: null,
        artifactsVisible: false,
        artifactsLoading: false,
        artifacts: [],
        artifactJob: null,
        downloadingArtifactId: null,
        activeTab: 'tasks',
    }

    componentDidMount() {
        this.loadProjects()
        this.loadList()
    }

    loadProjects = () => getAppBuildProjects()
        .then(projects => this.setState({ projects: projects || [] }))
        .catch(() => {})

    loadList = (pageNum = this.state.pagination.current, pageSize = this.state.pagination.pageSize) => {
        this.setState({ loading: true })
        return getAppBuildTasks({
            projectKey: this.state.projectKey,
            status: this.state.status,
            pageNum,
            pageSize,
        }).then(page => {
            const result = page || {}
            this.setState({
                dataSource: result.list || [],
                pagination: {
                    current: result.pageNum || pageNum,
                    pageSize: result.pageSize || pageSize,
                    total: result.total || 0,
                },
            })
        }).catch(() => {})
            .finally(() => this.setState({ loading: false }))
    }

    openCreate = () => {
        const first = this.state.projects[0]
        this.setState({ createVisible: true, createProjectKey: first && first.projectKey, validation: null }, () => {
            if (!this.formRef.current) return
            this.formRef.current.resetFields()
            if (first) {
                this.formRef.current.setFieldsValue({
                    projectKey: first.projectKey,
                    sourceRef: first.defaultBranch,
                })
            }
        })
    }

    selectedProject = (projectKey) => this.state.projects.find(item => item.projectKey === projectKey)

    onProjectChange = (projectKey) => {
        const project = this.selectedProject(projectKey)
        this.setState({ createProjectKey: projectKey, validation: null })
        this.formRef.current.setFieldsValue({
            profileKey: undefined,
            targetKeys: [],
            sourceRef: project ? project.defaultBranch : undefined,
        })
    }

    taskPayload = (values) => ({
        projectKey: values.projectKey,
        profileKey: values.profileKey,
        targetKeys: values.targetKeys,
        sourceRef: values.sourceRef.trim(),
        version: values.version ? values.version.trim() : undefined,
        remark: values.remark ? values.remark.trim() : undefined,
    })

    validateTask = () => this.formRef.current.validateFields().then(values => {
        this.setState({ validating: true, validation: null })
        return validateAppBuildTask(this.taskPayload(values)).then(validation => {
            this.setState({ validation })
            message.success('源码版本和构建能力校验通过')
            return validation
        }).finally(() => this.setState({ validating: false }))
    }).catch(() => null)

    createTask = () => this.formRef.current.validateFields().then(values => {
        const payload = this.taskPayload(values)
        const confirmed = this.state.validation
        const isSameValidation = confirmed
            && confirmed.projectKey === payload.projectKey
            && confirmed.profileKey === payload.profileKey
            && confirmed.sourceRef === payload.sourceRef
            && JSON.stringify(confirmed.targetKeys || []) === JSON.stringify(payload.targetKeys || [])
        const validationPromise = isSameValidation ? Promise.resolve(confirmed) : validateAppBuildTask(payload)
        this.setState({ creating: true })
        return validationPromise.then(validation => {
            this.setState({ validation })
            return createAppBuildTask({
                ...payload,
                requestId: `app-build-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
            })
        }).then(task => {
            message.success(`构建任务 ${task.taskNo} 已创建`)
            this.setState({ createVisible: false })
            this.loadList(1, this.state.pagination.pageSize)
        }).finally(() => this.setState({ creating: false }))
    }).catch(() => {})

    openDetail = (record) => {
        this.setState({ detailVisible: true, detailLoading: true, detail: null })
        getAppBuildTaskDetail(record.taskNo).then(detail => this.setState({ detail }))
            .catch(() => {})
            .finally(() => this.setState({ detailLoading: false }))
    }

    refreshDetail = () => {
        if (this.state.detail) this.openDetail(this.state.detail)
    }

    openLogs = (job) => {
        this.setState({ logsVisible: true, logsLoading: true, logs: [], logJob: job })
        getAppBuildJobLogs(job.id, { afterSequence: 0, limit: 2000 })
            .then(logs => this.setState({ logs: logs || [] }))
            .catch(() => {})
            .finally(() => this.setState({ logsLoading: false }))
    }

    openArtifacts = (job) => {
        this.setState({ artifactsVisible: true, artifactsLoading: true, artifacts: [], artifactJob: job })
        getAppBuildJobArtifacts(job.id)
            .then(artifacts => this.setState({ artifacts: artifacts || [] }))
            .catch(() => {})
            .finally(() => this.setState({ artifactsLoading: false }))
    }

    downloadArtifact = (artifact) => {
        this.setState({ downloadingArtifactId: artifact.id })
        return getAppBuildArtifactDownload(artifact.id).then(result => {
            if (!result || !result.url) throw new Error('下载地址为空')
            window.open(result.url, '_blank', 'noopener,noreferrer')
        }).catch(error => message.error(error && error.message ? error.message : '获取下载地址失败'))
            .finally(() => this.setState({ downloadingArtifactId: null }))
    }

    formatFileSize = (value) => {
        const bytes = Number(value || 0)
        if (bytes < 1024) return `${bytes} B`
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
        if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`
        return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`
    }

    statusTag = (status) => {
        const meta = STATUS[status] || { text: status || '-', color: 'default' }
        return <Tag color={meta.color}>{meta.text}</Tag>
    }

    columns = [
        { title: '任务编号', dataIndex: 'taskNo', width: 270, render: value => <Text copyable>{value}</Text> },
        { title: '项目', dataIndex: 'projectName', width: 140, render: (value, row) => value || row.projectKey },
        { title: '环境', dataIndex: 'profileKey', width: 100 },
        { title: '源码版本', dataIndex: 'sourceRef', width: 150, ellipsis: true },
        { title: '状态', dataIndex: 'status', width: 100, render: this.statusTag },
        { title: '创建时间', dataIndex: 'createTime', width: 170 },
        { title: '操作', key: 'action', width: 90, fixed: 'right', render: (_, row) => <Button type="link" onClick={() => this.openDetail(row)}>详情</Button> },
    ]

    renderCreateDrawer() {
        const project = this.selectedProject(this.state.createProjectKey) || {}
        return <Drawer
            title="创建应用构建任务"
            width={620}
            visible={this.state.createVisible}
            onClose={() => this.setState({ createVisible: false })}
            destroyOnClose
            footer={<div className="app-builder-drawer-footer">
                <Button onClick={() => this.setState({ createVisible: false })}>取消</Button>
                <Button loading={this.state.validating} onClick={this.validateTask}>校验</Button>
                <Button type="primary" loading={this.state.creating} onClick={this.createTask}>创建任务</Button>
            </div>}
        >
            <Alert
                type="info"
                showIcon
                message="独立构建，不影响现有自动发布"
                description="本功能不会修改示例项目、不会创建 Git Tag 或 GitHub Release；Electron 构建固定使用 --publish never。"
                style={{ marginBottom: 20 }}
            />
            <Form ref={this.formRef} layout="vertical">
                <Form.Item name="projectKey" label="项目" rules={[{ required: true, message: '请选择项目' }]}>
                    <Select onChange={this.onProjectChange}>
                        {this.state.projects.map(item => <Option key={item.projectKey} value={item.projectKey}>{item.projectName}</Option>)}
                    </Select>
                </Form.Item>
                <Form.Item name="profileKey" label="环境" rules={[{ required: true, message: '请选择环境' }]}>
                    <Select placeholder="请选择测试、预发布或生产环境">
                        {(project.profiles || []).map(item => <Option key={item.profileKey} value={item.profileKey}>{item.profileName}</Option>)}
                    </Select>
                </Form.Item>
                <Form.Item name="targetKeys" label="构建平台（可多选）" rules={[{ required: true, message: '至少选择一个平台' }]}>
                    <Select mode="multiple" placeholder="请选择平台">
                        {(project.targets || []).map(item => <Option key={item.targetKey} value={item.targetKey}>{item.targetName}</Option>)}
                    </Select>
                </Form.Item>
                <Form.Item name="sourceRef" label="分支 / Tag / Commit SHA" rules={[{ required: true, whitespace: true, message: '请输入源码版本' }]}>
                    <Input placeholder="例如 main、v1.2.0 或完整 40 位 Commit SHA" />
                </Form.Item>
                <Form.Item name="version" label="版本号（可选）"><Input placeholder="例如 1.2.0" /></Form.Item>
                <Form.Item name="remark" label="备注（可选）"><TextArea rows={3} maxLength={1000} /></Form.Item>
            </Form>
            {this.state.validation ? <Card size="small" title="校验结果">
                <Descriptions column={1} size="small">
                    <Descriptions.Item label="固定 Commit"><Text copyable>{this.state.validation.commitSha}</Text></Descriptions.Item>
                    <Descriptions.Item label="平台">{(this.state.validation.targetKeys || []).join('、')}</Descriptions.Item>
                </Descriptions>
            </Card> : null}
        </Drawer>
    }

    renderDetailDrawer() {
        const detail = this.state.detail
        const jobColumns = [
            { title: '平台', dataIndex: 'targetKey', width: 130 },
            { title: '执行器', dataIndex: 'executorKey', width: 130 },
            { title: '状态', dataIndex: 'status', width: 90, render: this.statusTag },
            { title: '阶段', dataIndex: 'phase', width: 120 },
            { title: '进度', dataIndex: 'progressPercent', width: 160, render: value => <Progress percent={Number(value || 0)} size="small" /> },
            { title: '错误', dataIndex: 'errorMessage', ellipsis: true },
            { title: '操作', key: 'actions', width: 150, render: (_, job) => <Space size={0}>
                <Button type="link" title="查看日志" icon={<FileTextOutlined />} onClick={() => this.openLogs(job)} />
                <Button type="link" title="查看产物" disabled={!Number(job.artifactCount || 0)} icon={<DownloadOutlined />} onClick={() => this.openArtifacts(job)} />
            </Space> },
        ]
        return <Drawer
            title="构建任务详情"
            width="86%"
            visible={this.state.detailVisible}
            onClose={() => this.setState({ detailVisible: false })}
            extra={<Button icon={<ReloadOutlined />} onClick={this.refreshDetail} loading={this.state.detailLoading}>刷新</Button>}
        >
            {detail ? <>
                <Descriptions bordered size="small" column={2}>
                    <Descriptions.Item label="任务编号"><Text copyable>{detail.taskNo}</Text></Descriptions.Item>
                    <Descriptions.Item label="状态">{this.statusTag(detail.status)}</Descriptions.Item>
                    <Descriptions.Item label="项目">{detail.projectKey}</Descriptions.Item>
                    <Descriptions.Item label="环境">{detail.profileKey}</Descriptions.Item>
                    <Descriptions.Item label="源码版本">{detail.sourceRef}</Descriptions.Item>
                    <Descriptions.Item label="固定 Commit"><Text copyable>{detail.commitSha}</Text></Descriptions.Item>
                    <Descriptions.Item label="目标平台" span={2}>{(detail.targets || []).map(item => <Tag key={item}>{item}</Tag>)}</Descriptions.Item>
                    <Descriptions.Item label="备注" span={2}>{detail.remark || '-'}</Descriptions.Item>
                </Descriptions>
                <Title level={4} style={{ marginTop: 24 }}>平台任务</Title>
                <Table rowKey="id" columns={jobColumns} dataSource={detail.jobs || []} pagination={false} scroll={{ x: 950 }} />
            </> : null}
        </Drawer>
    }

    render() {
        return <div className="app-builder-page">
            <Card bordered={false}>
                <Row justify="space-between" align="middle" gutter={[16, 16]}>
                    <Col><Title level={3} style={{ margin: 0 }}>应用构建</Title><Text type="secondary">统一创建 macOS、Windows 和 Linux 安装包</Text></Col>
                    {this.state.activeTab === 'tasks' ? <Col><Space><Button icon={<ReloadOutlined />} onClick={() => this.loadList()}>刷新</Button><Button type="primary" icon={<PlusOutlined />} onClick={this.openCreate}>新建构建</Button></Space></Col> : null}
                </Row>
                <Tabs activeKey={this.state.activeTab} onChange={activeTab => this.setState({ activeTab })}>
                    <TabPane tab="构建任务" key="tasks">
                        <Space wrap style={{ margin: '6px 0 16px' }}>
                            <Select allowClear placeholder="全部项目" style={{ width: 180 }} value={this.state.projectKey} onChange={projectKey => this.setState({ projectKey }, () => this.loadList(1))}>
                                {this.state.projects.map(item => <Option key={item.projectKey} value={item.projectKey}>{item.projectName}</Option>)}
                            </Select>
                            <Select allowClear placeholder="全部状态" style={{ width: 150 }} value={this.state.status} onChange={status => this.setState({ status }, () => this.loadList(1))}>
                                {Object.keys(STATUS).map(key => <Option key={key} value={key}>{STATUS[key].text}</Option>)}
                            </Select>
                        </Space>
                        <Table
                            rowKey="taskNo"
                            loading={this.state.loading}
                            columns={this.columns}
                            dataSource={this.state.dataSource}
                            scroll={{ x: 1100 }}
                            pagination={this.state.pagination}
                            onChange={pagination => this.loadList(pagination.current, pagination.pageSize)}
                        />
                    </TabPane>
                    <TabPane tab="项目配置" key="projects">
                        <AppBuildProjectConfig onProjectsChanged={this.loadProjects} />
                    </TabPane>
                </Tabs>
            </Card>
            {this.renderCreateDrawer()}
            {this.renderDetailDrawer()}
            <Modal
                title={`构建日志${this.state.logJob ? ` · ${this.state.logJob.targetKey}` : ''}`}
                width="82%"
                visible={this.state.logsVisible}
                footer={null}
                onCancel={() => this.setState({ logsVisible: false })}
            >
                <div className="app-builder-log">
                    {this.state.logsLoading ? '加载中…' : this.state.logs.length
                        ? this.state.logs.map(item => <div key={item.sequenceNo}><span>{item.logTime || ''} [{item.level}] </span>{item.message}</div>)
                        : '暂无日志'}
                </div>
            </Modal>
            <Modal
                title={`构建产物${this.state.artifactJob ? ` · ${this.state.artifactJob.targetKey}` : ''}`}
                width={900}
                visible={this.state.artifactsVisible}
                footer={null}
                onCancel={() => this.setState({ artifactsVisible: false })}
            >
                <Table
                    rowKey="id"
                    loading={this.state.artifactsLoading}
                    dataSource={this.state.artifacts}
                    pagination={false}
                    columns={[
                        { title: '文件名', dataIndex: 'fileName', ellipsis: true },
                        { title: '类型', dataIndex: 'artifactType', width: 110 },
                        { title: '大小', dataIndex: 'fileSize', width: 110, render: this.formatFileSize },
                        { title: 'SHA-256', dataIndex: 'sha256', width: 160, ellipsis: true, render: value => <Text copyable={{ text: value }}>{value}</Text> },
                        { title: '操作', key: 'download', width: 90, render: (_, artifact) => <Button
                            type="link"
                            icon={<DownloadOutlined />}
                            loading={this.state.downloadingArtifactId === artifact.id}
                            onClick={() => this.downloadArtifact(artifact)}
                        >下载</Button> },
                    ]}
                />
            </Modal>
        </div>
    }
}
